import React from 'react';
import ComparisonRenderer from '../ComparisonRenderer';
import { VersionLogFieldPanelRecord } from '../../version-log/records/VersionLogFieldPanelRecord';
import { ComparisonContext, ComparisonSideData, useComparisonContext } from '../hooks/useComparisonContext';
import { CompareSide } from '../diffHighlight';
import { SectionOuter, SectionCard, CardHeader } from './comparisonCells';

const hasItems = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

/**
 * Number of field rows the panel renders in each card. Mirrors the renderers' skip rule: files/image
 * fields empty in both versions render nothing (so they must not be counted), everything else is one row.
 */
const countFieldRows = (children: any[], left: ComparisonSideData, right: ComparisonSideData): number =>
  children.reduce((total: number, child: any) => {
    if (child.__typename === 'keyValueTable') {
      return total + (child.rows?.length ?? 0);
    }
    if (child.__typename === 'filesList' || child.__typename === 'image') {
      const rendered = hasItems(left.data?.[child.propertyId]) || hasItems(right.data?.[child.propertyId]);
      return total + (rendered ? 1 : 0);
    }
    return total;
  }, 0);

const FieldPanelRenderer: React.FC<VersionLogFieldPanelRecord> = ({ panelHeader, children }) => {
  const context = useComparisonContext();

  if (!Array.isArray(children)) {
    return null;
  }

  // 1 header row + the field rows; both cards share these tracks so their rows stay aligned.
  const rowCount = 1 + countFieldRows(children as any[], context.left, context.right);
  const sides: CompareSide[] = ['left', 'right'];

  return (
    <SectionOuter style={{ gridTemplateRows: `repeat(${rowCount}, auto)` }}>
      {sides.map((side, index) => (
        <SectionCard key={side} style={{ gridColumn: index + 1 }}>
          <CardHeader>{panelHeader}</CardHeader>
          <ComparisonContext.Provider value={{ ...context, side }}>
            <ComparisonRenderer schema={children} />
          </ComparisonContext.Provider>
        </SectionCard>
      ))}
    </SectionOuter>
  );
};

export default FieldPanelRenderer;
